import React, { useEffect, useState, useCallback } from 'react';
import { ExternalLink, FileText, Loader2, Mail, Sparkles, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';

function newsletterUrlFromEnv(): string {
  return import.meta.env.VITE_NEWSLETTER_URL?.trim() ?? '';
}

function defaultSubject(): string {
  return 'Newsletter';
}

function defaultBody(subscribeLink: string, recipientName: string | null | undefined): string {
  const greeting = recipientName?.trim() ? `Hi ${recipientName.trim()},` : 'Hi,';
  const url = subscribeLink.trim();
  const urlBlock =
    url ||
    '(Add your subscribe link in the field above, then tap Rebuild message from link + greeting.)';
  return `${greeting}\n\nI have a newsletter coming out and thought you might want to subscribe — here’s the signup page:\n${urlBlock}\n\nI also wanted to share an article I wrote that could be useful given our recent conversations (see link below when you add it from the assistant on the right).\n\nThanks,`;
}

type RecentTranscriptRow = {
  id: string;
  title: string;
  content: string;
  summary: string | null;
  meeting_date: string;
};

type BeehiivPostRow = {
  id: string;
  title: string;
  subtitle: string;
  web_url: string;
  status: string;
};

type SuggestionResult = {
  chosen_index: number;
  article_id: string;
  title: string;
  subtitle: string;
  web_url: string;
  reason: string;
  suggested_sentence: string;
};

interface InviteToNewsletterModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Used for mailto “To” and for loading transcripts where this email appears in participants */
  toEmail: string;
  recipientName: string | null | undefined;
}

function transcriptSnippet(row: RecentTranscriptRow, maxChars: number): string {
  const fromSummary = row.summary?.trim();
  if (fromSummary) {
    return fromSummary.length <= maxChars ? fromSummary : `${fromSummary.slice(0, maxChars - 1)}…`;
  }
  const c = row.content?.trim() ?? '';
  if (c.length <= maxChars) return c;
  return `${c.slice(0, maxChars - 1)}…`;
}

const InviteToNewsletterModal: React.FC<InviteToNewsletterModalProps> = ({
  isOpen,
  onClose,
  toEmail,
  recipientName,
}) => {
  const [subject, setSubject] = useState(defaultSubject);
  const [subscribeLink, setSubscribeLink] = useState('');
  const [body, setBody] = useState('');

  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState<string | null>(null);
  const [recentTranscripts, setRecentTranscripts] = useState<RecentTranscriptRow[]>([]);
  const [beehiivPosts, setBeehiivPosts] = useState<BeehiivPostRow[]>([]);

  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<SuggestionResult | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const url = newsletterUrlFromEnv();
    setSubscribeLink(url);
    setSubject(defaultSubject());
    setBody(defaultBody(url, recipientName));
    setSuggestion(null);
    setSuggestError(null);
    setContextError(null);
  }, [isOpen, recipientName]);

  useEffect(() => {
    if (!isOpen) {
      setRecentTranscripts([]);
      setBeehiivPosts([]);
      return;
    }
    const email = toEmail.trim();
    if (!email) return;

    let cancelled = false;
    setContextLoading(true);
    setContextError(null);

    void (async () => {
      try {
        const [txResult, postInv] = await Promise.all([
          supabase.rpc('transcripts_for_participant_email', { p_email: email }),
          supabase.functions.invoke<{ posts?: unknown[]; error?: string }>('fetch-beehiiv-posts', {
            body: { limit: 40, page: 1, status: 'all' },
          }),
        ]);

        if (txResult.error) throw txResult.error;
        const rows = (txResult.data ?? []) as RecentTranscriptRow[];
        const top3 = rows.slice(0, 3);

        const { data: postData, error: postErr } = postInv;
        if (postErr) throw new Error(postErr.message || 'Failed to load beehiiv posts');
        if (postData?.error) throw new Error(postData.error);

        const rawPosts = Array.isArray(postData?.posts) ? postData.posts : [];
        const posts: BeehiivPostRow[] = [];
        for (const p of rawPosts) {
          if (!p || typeof p !== 'object') continue;
          const o = p as Record<string, unknown>;
          const web_url = String(o.web_url ?? o.webUrl ?? '').trim();
          const title = String(o.title ?? '').trim();
          if (!web_url || !title) continue;
          posts.push({
            id: String(o.id ?? ''),
            title,
            subtitle: String(o.subtitle ?? o.sub_title ?? '').trim(),
            web_url,
            status: String(o.status ?? '').trim(),
          });
        }

        if (cancelled) return;
        setRecentTranscripts(top3);
        setBeehiivPosts(posts);
      } catch (e: unknown) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : 'Could not load transcripts or posts';
          setContextError(msg);
          setRecentTranscripts([]);
          setBeehiivPosts([]);
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setContextLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, toEmail]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const applyLinkToMessage = useCallback(() => {
    setBody(defaultBody(subscribeLink, recipientName));
  }, [subscribeLink, recipientName]);

  const runSuggestArticle = useCallback(async () => {
    if (recentTranscripts.length === 0) {
      toast.error('No recent transcripts for this email — nothing to match.');
      return;
    }
    if (beehiivPosts.length === 0) {
      toast.error('No beehiiv posts returned — check API key and publication.');
      return;
    }

    setSuggestLoading(true);
    setSuggestError(null);
    setSuggestion(null);

    try {
      const transcripts = recentTranscripts.map((t) => ({
        title: t.title,
        meeting_date: t.meeting_date,
        summary: (t.summary ?? '').slice(0, 3500),
        excerpt: (t.content ?? '').slice(0, 3500),
      }));
      const articles = beehiivPosts.map((p) => ({
        id: p.id,
        title: p.title,
        subtitle: p.subtitle,
        web_url: p.web_url,
      }));

      const { data, error } = await supabase.functions.invoke<SuggestionResult & { error?: string }>(
        'newsletter-invite-suggest',
        { body: { transcripts, articles } }
      );
      if (error) throw new Error(error.message || 'Suggestion request failed');
      const d = data as (SuggestionResult & { error?: string }) | null;
      if (!d || typeof d !== 'object') throw new Error('Empty response from suggestion service');
      if (typeof d.error === 'string' && d.error) throw new Error(d.error);
      if (!d.web_url || !d.title) throw new Error('Unexpected response from suggestion service');
      setSuggestion(d);
      toast.success('Suggested article ready');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Suggestion failed';
      setSuggestError(msg);
      toast.error(msg);
    } finally {
      setSuggestLoading(false);
    }
  }, [recentTranscripts, beehiivPosts]);

  const insertSuggestionIntoBody = useCallback(() => {
    if (!suggestion) return;
    const addition = `\n\n${suggestion.suggested_sentence}\n${suggestion.web_url}\n`;
    setBody((prev) => `${prev.trim()}${addition}`);
  }, [suggestion]);

  const openMailto = useCallback(() => {
    const to = toEmail.trim();
    if (!to) return;
    const sub = subject.trim() || defaultSubject();
    const href = `mailto:${to}?subject=${encodeURIComponent(sub)}&body=${encodeURIComponent(body)}`;
    if (href.length > 2200) {
      window.alert('Message is too long for a mailto link. Shorten the text and try again.');
      return;
    }
    window.location.href = href;
    onClose();
  }, [toEmail, subject, body, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-6xl flex-col overflow-hidden bg-white shadow-xl dark:bg-gray-800 min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-newsletter-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4 dark:border-gray-700">
          <div className="flex min-w-0 items-center gap-3">
            <div className="shrink-0 rounded-lg bg-emerald-500/10 p-2 text-emerald-600 dark:text-emerald-400">
              <Mail className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0">
              <h2 id="invite-newsletter-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                Invite to newsletter
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Loads your 3 most recent transcripts for this email and beehiiv posts — then AI can pick an article
                link to add.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2 xl:gap-8">
            <div className="min-w-0 space-y-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">To</p>
                <p className="mt-1 break-all font-mono text-sm text-gray-900 dark:text-gray-100">
                  {toEmail.trim() || '—'}
                </p>
              </div>

              <div>
                <label htmlFor="invite-newsletter-link" className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Newsletter subscribe link
                </label>
                <input
                  id="invite-newsletter-link"
                  type="url"
                  value={subscribeLink}
                  onChange={(e) => setSubscribeLink(e.target.value)}
                  placeholder="https://…"
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Prefilled from <code className="rounded bg-gray-100 px-1 dark:bg-gray-900">VITE_NEWSLETTER_URL</code>{' '}
                  when set.
                </p>
                <button
                  type="button"
                  onClick={applyLinkToMessage}
                  className="mt-2 text-xs font-medium text-emerald-700 hover:underline dark:text-emerald-400"
                >
                  Rebuild message from link + greeting
                </button>
              </div>

              <div>
                <label htmlFor="invite-newsletter-subject" className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Subject
                </label>
                <input
                  id="invite-newsletter-subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label htmlFor="invite-newsletter-body" className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Message
                </label>
                <textarea
                  id="invite-newsletter-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={14}
                  className="mt-1 min-h-[12rem] w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm leading-relaxed text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            <div className="min-w-0 space-y-4 border-t border-gray-200 pt-4 xl:border-l xl:border-t-0 xl:pl-8 xl:pt-0 dark:border-gray-700">
              {contextLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Loader2 className="h-5 w-5 shrink-0 animate-spin text-emerald-600" aria-hidden />
                  Loading transcripts and beehiiv posts…
                </div>
              ) : contextError ? (
                <p className="text-sm text-red-600 dark:text-red-400">{contextError}</p>
              ) : (
                <>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Recent transcripts (up to 3)</h3>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      Same lookup as connection transcripts: your email appears in meeting participants.
                    </p>
                    {recentTranscripts.length === 0 ? (
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">None found for this address.</p>
                    ) : (
                      <ul className="mt-2 space-y-2">
                        {recentTranscripts.map((t) => (
                          <li
                            key={t.id}
                            className="rounded-lg border border-gray-200 bg-gray-50/90 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900/40"
                          >
                            <div className="flex items-start gap-2">
                              <FileText className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden />
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-gray-900 dark:text-gray-100">{t.title || 'Untitled'}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {t.meeting_date ? new Date(t.meeting_date).toLocaleDateString() : '—'}
                                </p>
                                <p className="mt-1 text-xs leading-snug text-gray-600 dark:text-gray-300">
                                  {transcriptSnippet(t, 320)}
                                </p>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Beehiiv posts loaded ({beehiivPosts.length})
                    </h3>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      Recent publication posts from <code className="rounded bg-gray-100 px-1 dark:bg-gray-900">fetch-beehiiv-posts</code>.
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void runSuggestArticle()}
                      disabled={suggestLoading || recentTranscripts.length === 0 || beehiivPosts.length === 0}
                      className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {suggestLoading ? (
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                      ) : (
                        <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
                      )}
                      Suggest best article (AI)
                    </button>
                  </div>

                  {suggestError ? (
                    <p className="text-sm text-red-600 dark:text-red-400">{suggestError}</p>
                  ) : null}

                  {suggestion ? (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 dark:border-emerald-800 dark:bg-emerald-950/30">
                      <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-200">
                        Suggested article
                      </p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{suggestion.title}</p>
                      {suggestion.subtitle ? (
                        <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">{suggestion.subtitle}</p>
                      ) : null}
                      <a
                        href={suggestion.web_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1 break-all text-xs font-medium text-emerald-700 hover:underline dark:text-emerald-400"
                      >
                        {suggestion.web_url}
                        <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
                      </a>
                      <p className="mt-2 text-xs text-gray-700 dark:text-gray-300">{suggestion.reason}</p>
                      <p className="mt-2 text-xs italic text-gray-600 dark:text-gray-400">
                        &ldquo;{suggestion.suggested_sentence}&rdquo;
                      </p>
                      <button
                        type="button"
                        onClick={insertSuggestionIntoBody}
                        className="mt-3 text-sm font-medium text-emerald-800 underline hover:no-underline dark:text-emerald-200"
                      >
                        Insert sentence + link into message
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 bg-gray-50/90 px-4 py-3 sm:px-6 dark:border-gray-700 dark:bg-gray-900/50">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={openMailto}
            disabled={!toEmail.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Mail className="h-4 w-4" aria-hidden />
            Open in email app
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteToNewsletterModal;
